import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { 
  CreditCard, Search, ShoppingCart, User, Ticket, 
  Loader2, Check, AlertCircle, DollarSign, Tag,
  CalendarDays, Clock, Users, Package
} from 'lucide-react';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';

const StudioPOSPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [classPasses, setClassPasses] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientPaymentMethods, setClientPaymentMethods] = useState([]);
  const [clientClassPasses, setClientClassPasses] = useState([]);
  
  // Mode: 'classpass' or 'booking'
  const [posMode, setPosMode] = useState('classpass');
  
  // ClassPass purchase state
  const [selectedPass, setSelectedPass] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showNoCardDialog, setShowNoCardDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherValidation, setVoucherValidation] = useState(null);
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  
  // Class booking state
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [bookingPaymentType, setBookingPaymentType] = useState(null); // 'classpass', 'card'
  const [selectedClientPass, setSelectedClientPass] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Iter-46ab: scope ClassPasses to the owner's own studio. The
      // generic /api/class-passes endpoint returns every active pass
      // platform-wide, which surfaced passes from OTHER studios in the
      // POS dropdown and made the backend respond with 403 "Not
      // authorised for this studio" when the owner picked one.
      const studio = await apiGet('/api/studios/me').catch(() => null);
      const passesUrl = studio?.id
        ? `/api/class-passes?studio_id=${studio.id}`
        : '/api/class-passes';
      const [clientsRes, passesRes] = await Promise.all([
        apiGet('/api/studio/clients'),
        apiGet(passesUrl),
      ]);
      setClients(clientsRes || []);
      setClassPasses((passesRes || []).filter(p => p.is_active));
    } catch (e) {
      console.error('Failed to fetch data:', e);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientPaymentMethods = useCallback(async (clientId) => {
    try {
      const data = await apiGet('/api/studio/clients/{client_id}/payment-methods', { params: { client_id: clientId } });
      const methods = data || [];
      setClientPaymentMethods(methods);
      // Iter-46ad: alert the studio owner when the selected client has no
      // saved card on file. Without one, MIT (POS) charges cannot be
      // processed — they have to book online and tick "save card" first.
      if (methods.length === 0) {
        setShowNoCardDialog(true);
      }
    } catch (e) {
      console.error('Failed to fetch payment methods:', e);
      setClientPaymentMethods([]);
    }
  }, []);

  const fetchClientClassPasses = useCallback(async (clientId) => {
    try {
      const detail = await apiGet('/api/studio/clients/{client_id}', { params: { client_id: clientId } });
      // Iter-46 bugfix: only active passes with credits are bookable. Showing
      // expired passes here caused the POS booking endpoint to 404 on lookup.
      setClientClassPasses(
        (detail?.class_passes || []).filter(
          (p) => p.status === 'active' && (p.passes_remaining || 0) > 0
        )
      );
    } catch (e) {
      console.error('Failed to fetch client classpasses:', e);
      setClientClassPasses([]);
    }
  }, []);

  const fetchUpcomingClasses = useCallback(async (clientId) => {
    setClassesLoading(true);
    try {
      // Iter-46ac: scope to current studio (backend already enforces) and
      // exclude classes the client has already booked so the POS admin
      // can't accidentally double-book.
      const url = clientId
        ? `/api/studio/classes?exclude_booked_for_client=${encodeURIComponent(clientId)}`
        : '/api/studio/classes';
      const data = await apiGet(url);
      // Filter to upcoming classes only (next 14 days)
      const now = new Date();
      const twoWeeksLater = addDays(now, 14);
      const upcoming = (data || []).filter(cls => {
        const classDate = new Date(cls.start_time);
        return classDate > now && classDate <= twoWeeksLater;
      }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setUpcomingClasses(upcoming);
    } catch (e) {
      console.error('Failed to fetch classes:', e);
      setUpcomingClasses([]);
    } finally {
      setClassesLoading(false);
    }
  }, []);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSelectedPaymentMethod(null);
    setSelectedClientPass(null);
    setBookingPaymentType(null);
    setSelectedClass(null);
    fetchClientPaymentMethods(client.id);
    fetchClientClassPasses(client.id);
    // Iter-46ac: classes shown depend on the client (we hide ones already
    // booked), so reload them whenever the selection changes — but only if
    // we're in booking mode so we don't pre-fetch for the ClassPass flow.
    if (posMode === 'booking') {
      fetchUpcomingClasses(client.id);
    } else {
      // Force a refetch the next time the user enters booking mode.
      setUpcomingClasses([]);
    }
  };

  const handleModeChange = (mode) => {
    setPosMode(mode);
    setSelectedPass(null);
    setSelectedClass(null);
    setBookingPaymentType(null);
    setSelectedClientPass(null);
    clearVoucher();
    
    if (mode === 'booking') {
      // Always refetch — the upcomingClasses cache may be stale relative to
      // the current ``selectedClient``.
      fetchUpcomingClasses(selectedClient?.id);
    }
  };

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim() || !selectedPass) return;
    
    setValidatingVoucher(true);
    try {
      const data = await apiPost('/api/vouchers/validate', { body: {
        code: voucherCode,
        amount: selectedPass.price,
        purchase_type: 'classpass'
      } });
      setVoucherValidation(data);
      if (!data.valid) {
        toast.error(data.message || 'Invalid voucher');
      }
    } catch (e) {
      setVoucherValidation({ valid: false, message: 'Invalid voucher code' });
      toast.error('Invalid voucher code');
    } finally {
      setValidatingVoucher(false);
    }
  };

  const clearVoucher = () => {
    setVoucherCode('');
    setVoucherValidation(null);
  };

  const handleStartCheckout = () => {
    if (!selectedClient || !selectedPass) {
      toast.error('Please select a client and ClassPass');
      return;
    }
    setShowCheckoutDialog(true);
  };

  const handleProcessPayment = async () => {
    if (!selectedClient || !selectedPass || !selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setProcessing(true);
    try {
      const finalAmount = voucherValidation?.valid 
        ? voucherValidation.final_amount 
        : selectedPass.price;

      await apiPost('/api/studio/pos/charge', { body: {
        client_id: selectedClient.id,
        class_pass_id: selectedPass.id,
        payment_method_id: selectedPaymentMethod.id,
        amount: finalAmount,
        voucher_code: voucherValidation?.valid ? voucherCode : null
      } });

      toast.success(`Payment processed! ${selectedPass.name} added to ${selectedClient.first_name}'s account`);
      
      // Reset form
      setShowCheckoutDialog(false);
      setSelectedPass(null);
      setSelectedPaymentMethod(null);
      clearVoucher();
      
      // Refresh client data
      fetchClientClassPasses(selectedClient.id);
      
    } catch (e) {
      // Bug R31 UX: if the client's card was saved during a booking BEFORE
      // Iter-69 promoted it to CIT-on-file, the MIT charge fails with a
      // 400. Give the studio owner an actionable next step instead of the
      // raw processor error.
      const raw = (e?.message || '').toLowerCase();
      if (raw.includes('cannot be charged off-session') || raw.includes('previous payment')) {
        toast.error(
          `${selectedClient?.first_name || 'This client'}'s card was saved on an older booking and can't be charged from POS. ` +
          `Ask them to open Profile > Billing > Add card to re-register it — then try again.`,
          { duration: 12000 }
        );
      } else {
        toast.error(formatApiError(e, 'Failed to process payment'));
      }
    } finally {
      setProcessing(false);
    }
  };

  // Book class for client
  const handleStartBooking = () => {
    if (!selectedClient || !selectedClass) {
      toast.error('Please select a client and class');
      return;
    }
    setShowBookingDialog(true);
  };

  const handleProcessBooking = async () => {
    if (!selectedClient || !selectedClass) {
      toast.error('Missing client or class');
      return;
    }

    if (!bookingPaymentType) {
      toast.error('Please select a payment method');
      return;
    }

    setProcessing(true);
    try {
      // Book the class for the client
      await apiPost('/api/studio/pos/book-class', { body: {
        client_id: selectedClient.id,
        class_id: selectedClass.id,
        payment_type: bookingPaymentType,
        class_pass_id: bookingPaymentType === 'classpass' ? selectedClientPass?.id : null,
        payment_method_id: bookingPaymentType === 'card' ? selectedPaymentMethod?.id : null
      } });

      toast.success(`${selectedClass.name} booked for ${selectedClient.first_name}!`);
      
      // Reset form
      setShowBookingDialog(false);
      setSelectedClass(null);
      setBookingPaymentType(null);
      setSelectedClientPass(null);
      
      // Refresh data
      fetchClientClassPasses(selectedClient.id);
      fetchUpcomingClasses(selectedClient.id);
      
    } catch (e) {
      // Iter-46 bugfix: typed SDK throws ApiError with .body and .message,
      // not axios's .response.data.detail.
      toast.error(formatApiError(e, 'Failed to book class'));
    } finally {
      setProcessing(false);
    }
  };

  const filteredClients = clients.filter(c => 
    !clientSearch || 
    `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const getFinalPrice = () => {
    if (!selectedPass) return 0;
    return voucherValidation?.valid ? voucherValidation.final_amount : selectedPass.price;
  };

  // Get applicable client passes for selected class
  const getApplicableClientPasses = () => {
    if (!selectedClass) return [];
    return clientClassPasses.filter(pass => 
      pass.activity_type === selectedClass.activity_type || 
      pass.activity_type === 'general'
    );
  };

  // Check if class has available spots
  const getSpotsAvailable = (cls) => {
    return cls.max_capacity - (cls.current_bookings || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ShoppingCart className="w-8 h-8" />
            Point of Sale
          </h1>
          <p className="text-muted-foreground mt-1">
            Sell ClassPasses or book classes for your clients
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Client Selection */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Select Client
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="max-h-80 overflow-y-auto space-y-2">
                {filteredClients.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No clients found
                  </p>
                ) : (
                  filteredClients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedClient?.id === client.id 
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                          : 'hover:border-muted-foreground/50'
                      }`}
                    >
                      <p className="font-medium">{client.first_name} {client.last_name}</p>
                      <p className="text-sm text-muted-foreground">{client.email}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Client Info */}
              {selectedClient && (
                <div className="pt-4 border-t space-y-3">
                  <h4 className="font-medium text-sm">Client Details</h4>
                  
                  {/* ClassPasses */}
                  {clientClassPasses.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">ClassPasses:</p>
                      {clientClassPasses.map((pass) => (
                        <div key={pass.id} className="flex items-center justify-between text-sm bg-muted/50 p-2 rounded">
                          <span>{pass.class_pass_name}</span>
                          <Badge variant="secondary">{pass.passes_remaining} left</Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Saved Cards */}
                  {clientPaymentMethods.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Saved Cards:</p>
                      {clientPaymentMethods.map((pm) => (
                        <div key={pm.id} className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded" data-testid={`saved-card-${pm.id}`}>
                          <CreditCard className="w-4 h-4" />
                          <span className="font-mono">•••• {pm.card_last_four}</span>
                          <Badge variant="outline" className="text-xs capitalize">{pm.card_brand}</Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {clientClassPasses.length === 0 && clientPaymentMethods.length === 0 && (
                    <p className="text-sm text-amber-600">
                      No ClassPasses or saved cards on file
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right Column - Action Selection */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Transaction</CardTitle>
              <CardDescription>
                {selectedClient 
                  ? `Transaction for ${selectedClient.first_name} ${selectedClient.last_name}` 
                  : 'Select a client to begin'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedClient ? (
                <div className="text-center py-12 text-muted-foreground">
                  <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a client from the list to start a transaction</p>
                </div>
              ) : (
                <Tabs value={posMode} onValueChange={handleModeChange} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="classpass" className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Sell ClassPass
                    </TabsTrigger>
                    <TabsTrigger value="booking" className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      Book a Class
                    </TabsTrigger>
                  </TabsList>

                  {/* Sell ClassPass Tab */}
                  <TabsContent value="classpass" className="space-y-6">
                    {/* ClassPass Selection */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Select ClassPass</Label>
                      <div className="grid sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                        {classPasses.map((pass) => (
                          <div
                            key={pass.id}
                            onClick={() => { setSelectedPass(pass); clearVoucher(); }}
                            className={`p-4 rounded-lg border cursor-pointer transition-all ${
                              selectedPass?.id === pass.id 
                                ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                                : 'hover:border-muted-foreground/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium">{pass.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {pass.passes_count} classes • {pass.validity_days} days
                                </p>
                                {pass.is_membership && (
                                  <Badge variant="secondary" className="mt-1">Membership</Badge>
                                )}
                              </div>
                              <p className="text-lg font-bold text-primary">€{pass.price}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Voucher */}
                    {selectedPass && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Voucher Code (Optional)
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Enter voucher code"
                            value={voucherCode}
                            onChange={(e) => { setVoucherCode(e.target.value.toUpperCase()); setVoucherValidation(null); }}
                            className="flex-1"
                          />
                          <Button 
                            variant="outline" 
                            onClick={handleValidateVoucher}
                            disabled={!voucherCode || validatingVoucher}
                          >
                            {validatingVoucher ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                          </Button>
                        </div>
                        {voucherValidation && (
                          <div className={`text-sm flex items-center gap-2 ${voucherValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                            {voucherValidation.valid ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            {voucherValidation.valid 
                              ? `Discount applied: €${voucherValidation.discount_amount} off`
                              : voucherValidation.message}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Checkout Button */}
                    {selectedPass && (
                      <div className="pt-4 border-t">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-lg font-medium">Total:</span>
                          <div className="text-right">
                            {voucherValidation?.valid && (
                              <p className="text-sm text-muted-foreground line-through">€{selectedPass.price}</p>
                            )}
                            <p className="text-2xl font-bold text-primary">€{getFinalPrice()}</p>
                          </div>
                        </div>
                        <Button 
                          className="w-full" 
                          size="lg"
                          onClick={handleStartCheckout}
                          disabled={clientPaymentMethods.length === 0}
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          Proceed to Payment
                        </Button>
                        {clientPaymentMethods.length === 0 && (
                          <p className="text-sm text-amber-600 mt-2 text-center">
                            Client has no saved payment methods
                          </p>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  {/* Book a Class Tab */}
                  <TabsContent value="booking" className="space-y-6">
                    {classesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin" />
                      </div>
                    ) : (
                      <>
                        {/* Class Selection */}
                        <div className="space-y-3">
                          <Label className="text-base font-medium">Select Class (Next 14 Days)</Label>
                          {upcomingClasses.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                              <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                              <p>No upcoming classes scheduled</p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-72 overflow-y-auto">
                              {upcomingClasses.map((cls) => {
                                const spotsAvailable = getSpotsAvailable(cls);
                                const isFull = spotsAvailable <= 0;
                                
                                return (
                                  <div
                                    key={cls.id}
                                    onClick={() => !isFull && setSelectedClass(cls)}
                                    className={`p-4 rounded-lg border transition-all ${
                                      isFull 
                                        ? 'opacity-50 cursor-not-allowed bg-muted/30' 
                                        : selectedClass?.id === cls.id 
                                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20 cursor-pointer' 
                                          : 'hover:border-muted-foreground/50 cursor-pointer'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge variant="outline">{formatActivity(cls.activity_type)}</Badge>
                                          <span className="font-medium">{cls.name}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                          <span className="flex items-center gap-1">
                                            <CalendarDays className="w-3 h-3" />
                                            {format(new Date(cls.start_time), 'EEE, MMM d')}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {format(new Date(cls.start_time), 'h:mm a')}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <Users className="w-3 h-3" />
                                            {spotsAvailable} spots
                                          </span>
                                        </div>
                                        {cls.instructor_name && (
                                          <p className="text-sm text-muted-foreground mt-1">
                                            Instructor: {cls.instructor_name}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        {isFull ? (
                                          <Badge variant="secondary">Full</Badge>
                                        ) : (
                                          <span className="text-lg font-bold text-primary">
                                            €{cls.price}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Payment Method Selection for Booking */}
                        {selectedClass && (
                          <div className="space-y-3 pt-4 border-t">
                            <Label className="text-base font-medium">Payment Method</Label>
                            
                            {/* Option 1: Use ClassPass */}
                            {getApplicableClientPasses().length > 0 && (
                              <div className="space-y-2">
                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Ticket className="w-4 h-4" />
                                  Use a ClassPass:
                                </p>
                                {getApplicableClientPasses().map((pass) => (
                                  <div
                                    key={pass.id}
                                    onClick={() => {
                                      setBookingPaymentType('classpass');
                                      setSelectedClientPass(pass);
                                    }}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                      bookingPaymentType === 'classpass' && selectedClientPass?.id === pass.id
                                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                                        : 'hover:border-muted-foreground/50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="font-medium">{pass.class_pass_name}</p>
                                        <p className="text-sm text-muted-foreground">
                                          {pass.passes_remaining} classes remaining
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {bookingPaymentType === 'classpass' && selectedClientPass?.id === pass.id && (
                                          <Badge className="bg-primary">Selected</Badge>
                                        )}
                                        <Ticket className="w-5 h-5 text-primary" />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Option 2: Charge Card */}
                            {selectedClass.price > 0 && clientPaymentMethods.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                  <CreditCard className="w-4 h-4" />
                                  Charge saved card:
                                </p>
                                {clientPaymentMethods.map((pm) => (
                                  <div
                                    key={pm.id}
                                    onClick={() => {
                                      setBookingPaymentType('card');
                                      setSelectedPaymentMethod(pm);
                                    }}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                      bookingPaymentType === 'card' && selectedPaymentMethod?.id === pm.id
                                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                                        : 'hover:border-muted-foreground/50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CreditCard className="w-5 h-5" />
                                        <div>
                                          <p className="font-medium font-mono">•••• {pm.card_last_four}</p>
                                          <p className="text-sm text-muted-foreground capitalize">{pm.card_brand}</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {bookingPaymentType === 'card' && selectedPaymentMethod?.id === pm.id && (
                                          <Badge className="bg-primary">Selected</Badge>
                                        )}
                                        <span className="font-bold text-primary">€{selectedClass.price}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Free class */}
                            {selectedClass.price === 0 && getApplicableClientPasses().length === 0 && (
                              <div 
                                onClick={() => setBookingPaymentType('free')}
                                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                                  bookingPaymentType === 'free'
                                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                                    : 'hover:border-muted-foreground/50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">Free Class</p>
                                    <p className="text-sm text-muted-foreground">No payment required</p>
                                  </div>
                                  {bookingPaymentType === 'free' && (
                                    <Badge className="bg-primary">Selected</Badge>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* No payment options warning */}
                            {getApplicableClientPasses().length === 0 && clientPaymentMethods.length === 0 && selectedClass.price > 0 && (
                              <div className="p-4 rounded-lg border border-amber-300 bg-amber-50">
                                <div className="flex items-center gap-3">
                                  <AlertCircle className="w-5 h-5 text-amber-600" />
                                  <div>
                                    <p className="font-medium text-amber-800">No Payment Methods</p>
                                    <p className="text-sm text-amber-700">
                                      Client has no applicable ClassPass or saved cards for this class.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Book Button */}
                        {selectedClass && bookingPaymentType && (
                          <div className="pt-4">
                            <Button 
                              className="w-full" 
                              size="lg"
                              onClick={handleStartBooking}
                            >
                              <CalendarDays className="w-5 h-5 mr-2" />
                              Book {selectedClass.name} for {selectedClient.first_name}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ClassPass Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Payment</DialogTitle>
            <DialogDescription>
              Charge {selectedClient?.first_name}'s saved card
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Order Summary */}
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="font-medium">{selectedPass?.name}</p>
              <p className="text-sm text-muted-foreground">
                {selectedPass?.passes_count} classes • {selectedPass?.validity_days} days validity
              </p>
              <div className="flex justify-between mt-2 pt-2 border-t">
                <span>Total:</span>
                <span className="font-bold">€{getFinalPrice()}</span>
              </div>
            </div>

            {/* Card Selection */}
            <div className="space-y-2">
              <Label>Select Card</Label>
              {clientPaymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  onClick={() => setSelectedPaymentMethod(pm)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedPaymentMethod?.id === pm.id 
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                      : 'hover:border-muted-foreground/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5" />
                    <div>
                      <p className="font-medium font-mono">•••• {pm.card_last_four}</p>
                      <p className="text-sm text-muted-foreground capitalize">{pm.card_brand}</p>
                    </div>
                    {selectedPaymentMethod?.id === pm.id && (
                      <Check className="w-5 h-5 text-primary ml-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckoutDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleProcessPayment} 
              disabled={!selectedPaymentMethod || processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <DollarSign className="w-4 h-4 mr-2" />
              )}
              Charge €{getFinalPrice()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Class Booking Confirmation Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Booking</DialogTitle>
            <DialogDescription>
              Book this class for {selectedClient?.first_name} {selectedClient?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Booking Summary */}
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{formatActivity(selectedClass?.activity_type)}</Badge>
                <span className="font-medium">{selectedClass?.name}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CalendarDays className="w-4 h-4" />
                  {selectedClass && format(new Date(selectedClass.start_time), 'EEEE, MMMM d, yyyy')}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {selectedClass && format(new Date(selectedClass.start_time), 'h:mm a')}
                </span>
              </div>
              {selectedClass?.instructor_name && (
                <p className="text-sm text-muted-foreground">
                  Instructor: {selectedClass.instructor_name}
                </p>
              )}
            </div>

            {/* Payment Method */}
            <div className="p-3 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-1">Payment Method:</p>
              {bookingPaymentType === 'classpass' && (
                <div className="flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-primary" />
                  <span className="font-medium">{selectedClientPass?.class_pass_name}</span>
                  <span className="text-muted-foreground">(-1 credit)</span>
                </div>
              )}
              {bookingPaymentType === 'card' && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    <span className="font-medium font-mono">•••• {selectedPaymentMethod?.card_last_four}</span>
                  </div>
                  <span className="font-bold">€{selectedClass?.price}</span>
                </div>
              )}
              {bookingPaymentType === 'free' && (
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Free Class - No charge</span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleProcessBooking} 
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Confirm Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-46ad: alert when client has no saved card */}
      <Dialog open={showNoCardDialog} onOpenChange={setShowNoCardDialog}>
        <DialogContent className="max-w-md" data-testid="no-saved-card-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              No Saved Card on File
            </DialogTitle>
            <DialogDescription>
              This client does not have a saved card within the platform. In order to book a Class,
              ClassPass or Membership and pay by credit card, the client must have previously completed
              a booking online and saved a card for future use. You can proceed with booking a Class
              with a previously purchased ClassPass.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowNoCardDialog(false)} data-testid="no-saved-card-dialog-close">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default StudioPOSPage;
